import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';


export class ValidatorUtils {

  // language=JSRegexp
  static readonly NIP_REGEX: string = '^[0-9]{10}$';
  // language=JSRegexp
  static readonly REGON_REGEX: string = '^([0-9]{9}|[0-9]{14})$';
  // language=JSRegexp
  static readonly GLN_REGEX: string = '^[0-9]{12,13}$';

  static isNipValid(value: string): boolean {
    return value && this.isNipFormatValid(value) && this.isNipControlNumberValid(value);
  }

  static isNipFormatValid(value: string): boolean {
    return value && new RegExp(this.NIP_REGEX).test(value);
  }

  static isNipControlNumberValid(value: string): boolean {
    return value && this.nipHasValidChecksum(value);
  }

  static isRegonFormatValid(value: string): boolean {
    return value && new RegExp(this.REGON_REGEX).test(value);
  }

  static isRegonControlNumberValid(value: string): boolean {
    return value && this.regonHasValidChecksum(value);
  }

  static isGlnValid(value: string): boolean {
    return value && new RegExp(this.GLN_REGEX).test(value) && this.glnHasValidChecksum(value);
  }

  private static nipHasValidChecksum(value: string): boolean {
    if (value.slice) {
      const valueWithoutChecksumDigit = value.slice(0, -1);
      const checksum = this.nipCalculateChecksum(valueWithoutChecksumDigit);
      return Number.parseInt(value.charAt(value.length - 1)) === checksum;
    } else {
      return false;
    }
  }

  private static nipCalculateChecksum(value: string): number {
    const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
    let sum = 0;
    for (let i = 0; i < weights.length; i++) {
      const digit = Number.parseInt(value.charAt(i));
      sum += digit * weights[i];
    }
    return sum % 11;
  }

  private static regonHasValidChecksum(value: string): boolean {
    if (value.slice) {
      if (value.length == 9) {
        return this.regonHasValidChecksumWithinWeights(value, [8, 9, 2, 3, 4, 5, 6, 7]);
      } else if (value.length == 14) {
        return this.regonHasValidChecksumWithinWeights(value.slice(0, 9), [8, 9, 2, 3, 4, 5, 6, 7]) &&
          this.regonHasValidChecksumWithinWeights(value, [2, 4, 8, 5, 0, 9, 7, 3, 6, 1, 2, 4, 8]);
      }
    } else {
      return false;
    }
  }

  private static regonHasValidChecksumWithinWeights(value: string, weights: number[]): boolean {
    const valueWithoutChecksumDigit = value.slice(0, -1);
    const checksum = this.regonCalculateChecksum(valueWithoutChecksumDigit,weights);
    return Number.parseInt(value.charAt(value.length - 1)) === checksum % 10;
  }

  private static regonCalculateChecksum(value: string, weights: number[]): number {
    let sum = 0;
    for (let i = 0; i < weights.length; i++) {
      const digit = Number.parseInt(value.charAt(i));
      sum += digit * weights[i];
    }
    return sum % 11;
  }

  private static glnHasValidChecksum(value: string): boolean {
    const valueWithoutChecksumDigit = value.slice(0, -1);
    const checksum = this.glnCalculateChecksum(valueWithoutChecksumDigit);
    return Number.parseInt(value.charAt(value.length - 1)) === checksum;
  }

  private static glnCalculateChecksum(value: string) {
    const weights = [3, 1, 3, 1, 3, 1, 3, 1, 3, 1, 3, 1, 3, 1, 3, 1, 3];
    let sum = 0;
    const firstWeightToUse = weights.length - value.length;
    for (let i = 0, j = firstWeightToUse; i < value.length; i++ , j++) {
      const digit = Number.parseInt(value.charAt(i));
      sum += digit * weights[j];
    }
    const nextMultipleOfTen = this.findNextMultipleOfTen(sum);
    return nextMultipleOfTen - sum;
  }

  private static findNextMultipleOfTen(value: number) {
    let nextMultipleOfTen = value;
    while (nextMultipleOfTen % 10 !== 0) {
      nextMultipleOfTen++;
    }
    return nextMultipleOfTen;
  }

  public static setValueWithoutValidation(control: AbstractControl, value: any): void {
    this.executeWithoutValidation(control, () => control.setValue(value, { onlySelf: true, emitEvent: false }));
  }

  public static executeWithoutValidation(control: AbstractControl, fn: () => void): void {
    const validator = control.validator;
    const asyncValidator = control.asyncValidator;
    control.validator = null;
    control.asyncValidator = null;
    fn();
    control.validator = validator;
    control.asyncValidator = asyncValidator;
  }

  public static setValueWithoutAsyncValidation(control: AbstractControl, value: any): void {
    this.executeWithoutAsyncValidation(control, () => control.setValue(value, { onlySelf: false, emitEvent: false }));
  }

  public static executeWithoutAsyncValidation(control: AbstractControl, fn: () => void) {
    const asyncValidators = [];
    let parent = control;

    do {
      asyncValidators.push(parent.asyncValidator);
      parent.asyncValidator = null;
      parent = parent.parent;
    } while (parent);

    fn();

    parent = control;
    do {
      parent.asyncValidator = asyncValidators.shift();
      parent = parent.parent;
    } while (parent);
  }

  public static showNotValidControlsOnConsole(control: AbstractControl, path: string = '') {
    if (control.valid) {
      return;
    } else if (control instanceof UntypedFormControl) {
      console.warn('not_valid ' + path, control);
    } else if (control instanceof UntypedFormGroup) {
      Object.keys(control.controls).forEach(field => {
        const c = control.get(field);
        this.showNotValidControlsOnConsole(c, path + '/' + field);
      });
    } else if (control instanceof UntypedFormArray) {
      control.controls.forEach(
        (c, index) => this.showNotValidControlsOnConsole(c, path + '/' + index)
      );
    }
  }
}
